import { useCallback, useState } from "react";

import {
  isAcceptedMediaType,
  MEDIA_CONSTRAINTS,
  normalizeMimeType,
} from "@domain/media-constraints";

export interface PendingAttachments {
  files: File[];
  previews: string[];
  addFiles: (files: File[]) => void;
  removeFile: (index: number) => void;
  clear: () => void;
}

export interface PartitionResult {
  valid: File[];
  rejected: Array<{ name: string; reason: string }>;
}

export function partitionMediaFiles(files: File[]): PartitionResult {
  const valid: File[] = [];
  const rejected: Array<{ name: string; reason: string }> = [];
  for (const file of files) {
    const normalizedType = normalizeMimeType(file.type);
    if (!isAcceptedMediaType(normalizedType)) {
      rejected.push({ name: file.name, reason: `unsupported type: ${file.type || "unknown"}` });
      continue;
    }
    if (file.size > MEDIA_CONSTRAINTS.maxSizeBytes) {
      rejected.push({ name: file.name, reason: "exceeds 10MB" });
      continue;
    }
    valid.push(file);
  }
  return { valid, rejected };
}

export function usePendingAttachments(): PendingAttachments {
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);

  const addFiles = useCallback((incoming: File[]) => {
    if (!incoming.length) return;
    const { valid, rejected } = partitionMediaFiles(incoming);
    for (const r of rejected) {
      console.warn(`[attachments] rejected ${r.name}: ${r.reason}`);
    }
    if (!valid.length) return;

    setFiles((prev) => [...prev, ...valid]);

    for (const file of valid) {
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = () => {
          setPreviews((prev) => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      } else {
        setPreviews((prev) => [...prev, `file:${file.name}`]);
      }
    }
  }, []);

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clear = useCallback(() => {
    setFiles([]);
    setPreviews([]);
  }, []);

  return { files, previews, addFiles, removeFile, clear };
}
